import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { User, Mail, MessageSquare, Camera, Trash2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachProfile({ user, onLogout }) {
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Helper to convert relative avatar URLs to absolute
  const getAvatarUrl = (url) => {
    if (!url) return '';
    // If already absolute (starts with http), return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If relative, prepend current origin
    return `${window.location.origin}${url}`;
  };
  const fileInputRef = useRef(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [welcomeEnabled, setWelcomeEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [loadingWelcome, setLoadingWelcome] = useState(true);

  // Fetch welcome message + latest avatar on mount
  useEffect(() => {
    const init = async () => {
      try {
        const me = await axios.get(`${API}/auth/me`);
        if (me.data?.avatar_url) setAvatarUrl(me.data.avatar_url);
      } catch (_) { /* silent */ }
      try {
        const res = await axios.get(`${API}/coach/welcome-message`);
        if (res.data) {
          setWelcomeMessage(res.data.message || '');
          setWelcomeEnabled(res.data.enabled || false);
        }
      } catch (err) {
        console.log('No welcome message set yet');
      } finally {
        setLoadingWelcome(false);
      }
    };
    init();
  }, []);

  const displayName = `Coach ${formData.first_name || ''} ${formData.last_name || ''}`.trim().replace(/\s+/g, ' ');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/me`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim()
      });
      // Update localStorage so the name change appears immediately in navbars / bubbles
      if (res.data?.user) {
        const key = user.role === 'coach' ? 'coach_user' : 'client_user';
        localStorage.setItem(key, JSON.stringify(res.data.user));
      }
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWelcomeMessage = async () => {
    setSavingWelcome(true);
    try {
      await axios.put(`${API}/coach/welcome-message`, {
        message: welcomeMessage,
        enabled: welcomeEnabled
      });
      toast.success('Welcome message saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save welcome message');
    } finally {
      setSavingWelcome(false);
    }
  };

  const persistUserUpdate = (updates) => {
    const key = user?.role === 'coach' ? 'coach_user' : 'client_user';
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '{}');
      localStorage.setItem(key, JSON.stringify({ ...stored, ...updates }));
    } catch (_) { /* ignore */ }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image is too large. Max 5 MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/profile/avatar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = res.data?.avatar_url;
      setAvatarUrl(newUrl);
      persistUserUpdate({ avatar_url: newUrl });
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    if (!avatarUrl) return;
    if (!window.confirm('Remove your profile picture?')) return;
    setUploadingAvatar(true);
    try {
      await axios.delete(`${API}/profile/avatar`);
      setAvatarUrl('');
      persistUserUpdate({ avatar_url: null });
      toast.success('Profile picture removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove picture');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const initials = `${formData.first_name?.[0] || ''}${formData.last_name?.[0] || ''}`.trim().toUpperCase() || 'C';

  return (
    <div data-testid="coach-profile-page" className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Profile</h1>
          <p className="text-gray-600">Update your account info. Name changes reflect in client messages and dashboards.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#1a61e8]" />
              Display Name Preview
            </CardTitle>
            <CardDescription>Clients will see this in the chat bubble and messages page:</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-100 rounded-lg px-4 py-3 text-lg font-semibold text-gray-900">
              {displayName || 'Coach ?'}
            </div>
          </CardContent>
        </Card>

        {/* Profile Picture Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#1a61e8]" />
              Profile Picture
            </CardTitle>
            <CardDescription>Shown next to your messages in client chats. A round avatar — max 5 MB.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-5">
              <div
                data-testid="coach-avatar-preview"
                className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#1a61e8] to-[#0d3fa3] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 ring-2 ring-white shadow"
              >
                {avatarUrl ? (
                  <img src={getAvatarUrl(avatarUrl)} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="coach-avatar-file-input"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    data-testid="coach-avatar-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="bg-[#1a61e8] hover:bg-[#1553c9]"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {uploadingAvatar ? 'Uploading...' : (avatarUrl ? 'Change picture' : 'Upload picture')}
                  </Button>
                  {avatarUrl && (
                    <Button
                      type="button"
                      data-testid="coach-avatar-remove-btn"
                      variant="outline"
                      onClick={handleAvatarRemove}
                      disabled={uploadingAvatar}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">JPEG, PNG, WebP or GIF. A square photo crops best.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Edit Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                <Input
                  id="first_name"
                  data-testid="coach-profile-first-name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="e.g. Knut"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  data-testid="coach-profile-last-name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="(optional)"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="coach-profile-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <Button
                data-testid="coach-profile-save"
                type="submit"
                disabled={saving}
                className="bg-[#1a61e8] hover:bg-[#1553c9]"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Automated Welcome Message Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              <CardTitle>Automated Welcome Message</CardTitle>
            </div>
            <CardDescription>
              Automatically send a welcome message to new clients after they complete onboarding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingWelcome ? (
              <p className="text-gray-500">Loading...</p>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="welcome-message">Welcome Message Template</Label>
                  <Textarea
                    id="welcome-message"
                    data-testid="welcome-message-textarea"
                    value={welcomeMessage}
                    onChange={(e) => setWelcomeMessage(e.target.value)}
                    placeholder="Hey {{name}}! 👋&#10;&#10;Welcome to MealGuide! I'm excited to work with you over these 7 days.&#10;&#10;Here's what happens next:&#10;1. I'll create your personalized meal plan (ready in 24 hours)&#10;2. Log your weight daily&#10;3. Complete your weekly check-in on Wednesday/Thursday&#10;&#10;Feel free to message me anytime!&#10;&#10;- Coach {{coach_name}}"
                    rows={10}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    💡 Use <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{name}}'}</code> for client&apos;s first name and <code className="bg-gray-100 px-1 py-0.5 rounded">{'{{coach_name}}'}</code> for your name
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Send automatically after onboarding</p>
                    <p className="text-sm text-gray-600">New clients will receive this message immediately</p>
                  </div>
                  <Switch
                    data-testid="welcome-message-enabled-switch"
                    checked={welcomeEnabled}
                    onCheckedChange={setWelcomeEnabled}
                  />
                </div>

                <Button 
                  data-testid="save-welcome-message-btn"
                  onClick={handleSaveWelcomeMessage}
                  disabled={savingWelcome}
                  className="w-full"
                >
                  {savingWelcome ? 'Saving...' : 'Save Welcome Message'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
